import { normalizeIsraeliPhone } from '../phone';
import {
  CONSENT_WORDING_VERSION, fail, newId, ok, type Appointment, type Client, type ConsentPurpose, type ConsentRecord, type ConsentSource,
  type Id, type Message, type Result, type WaitlistEntry,
} from '../types';
import { activeConsent } from '../reminders';
import { makeToken, type Deps } from './context';
import { cancelWithoutFee, isFutureLive } from './erasure';

const NIQQUD = /[֑-ׇ]/g;
const normalizeText = (s: string): string => s.normalize('NFC').replace(NIQQUD, '').replace(/["'׳״]/g, '').toLowerCase().trim();

export interface UpsertClientInput {
  businessId: Id;
  id?: Id;
  name: string;
  phone: string;
  workNotes?: string;
  sensitiveNotes?: string;
  tags?: string[];
}
export interface ClientExport {
  exportedAt: string;
  client: Omit<Client, 'sensitiveNotes'> & { sensitiveNotes?: string };
  appointments: Appointment[];
  consents: ConsentRecord[];
  messages: Message[];
  waitlistEntries: WaitlistEntry[];
}

export function clientUseCases(d: Deps) {
  const now = (): string => d.clock.nowIso();

  async function findByPhone(businessId: Id, e164: string): Promise<Client | undefined> {
    return (await d.storage.repo('clients').list({ where: { phone: e164 }, filter: (c) => c.businessId === businessId && !c.deletedAt }))[0];
  }

  /** Finds the client with this E.164 phone or creates one. Used by every booking path. */
  async function findOrCreate(businessId: Id, name: string, e164: string, extra: Partial<Pick<Client, 'workNotes' | 'sensitiveNotes' | 'tags'>> = {}): Promise<{ client: Client; created: boolean }> {
    const existing = await findByPhone(businessId, e164);
    if (existing) return { client: existing, created: false };
    const t = now();
    const client: Client = { id: newId(d.clock.now()), businessId, createdAt: t, updatedAt: t, name: name.trim(), phone: e164, workNotes: extra.workNotes ?? '', sensitiveNotes: extra.sensitiveNotes ?? '', tags: extra.tags ?? [] };
    await d.storage.repo('clients').put(client);
    return { client, created: true };
  }

  async function upsertClient(input: UpsertClientInput): Promise<Result<{ client: Client; created: boolean }, 'invalid_phone' | 'invalid_name' | 'business_not_found' | 'phone_in_use' | 'client_not_found'>> {
    if (!(await d.storage.repo('businesses').get(input.businessId))) return fail('business_not_found');
    const phone = normalizeIsraeliPhone(input.phone);
    if (!phone.ok) return fail('invalid_phone');
    if (input.name.trim().length < 2) return fail('invalid_name');
    const same = await findByPhone(input.businessId, phone.e164);
    const cur = input.id ? await d.storage.repo('clients').get(input.id) : same;
    if (input.id && !cur) return fail('client_not_found');
    if (same && cur && same.id !== cur.id) return fail('phone_in_use');
    if (!cur) {
      const extra = { ...(input.workNotes !== undefined ? { workNotes: input.workNotes } : {}), ...(input.sensitiveNotes !== undefined ? { sensitiveNotes: input.sensitiveNotes } : {}), ...(input.tags ? { tags: input.tags } : {}) };
      return ok(await findOrCreate(input.businessId, input.name, phone.e164, extra));
    }
    const next: Client = {
      ...cur, name: input.id || cur.name === '' ? input.name.trim() : cur.name, phone: phone.e164,
      workNotes: input.workNotes ?? cur.workNotes, sensitiveNotes: input.sensitiveNotes ?? cur.sensitiveNotes, tags: input.tags ?? cur.tags, updatedAt: now(),
    };
    await d.storage.repo('clients').put(next);
    return ok({ client: next, created: false });
  }

  /** Partial name (any word, niqqud/quotes ignored) or partial phone (digits, 050.. or +972..). */
  async function searchClients(businessId: Id, query: string, limit = 20): Promise<Client[]> {
    const all = (await d.storage.repo('clients').list({ where: { businessId } })).filter((c) => !c.deletedAt);
    const q = normalizeText(query);
    const digits = query.replace(/\D/g, '').replace(/^972/, '').replace(/^0+/, '');
    const matches = q === '' ? all : all.filter((c) => {
      const name = normalizeText(c.name);
      const nameHit = q.split(/\s+/).every((tok) => name.includes(tok));
      const phoneHit = digits.length >= 2 && c.phone.replace(/\D/g, '').replace(/^972/, '').includes(digits);
      return nameHit || phoneHit;
    });
    const rank = (c: Client): number => (q !== '' && normalizeText(c.name).startsWith(q) ? 0 : 1);
    return matches.sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name, 'he')).slice(0, limit);
  }

  async function getClientHistory(clientId: Id): Promise<Appointment[]> {
    const rows = await d.storage.repo('appointments').list({ where: { clientId } });
    return rows.sort((a, b) => b.start.localeCompare(a.start));
  }

  // ------------------------------------------------------------ consent ledger

  async function recordConsent(input: { clientId: Id; purpose: ConsentPurpose; source: ConsentSource; wordingVersion?: string }): Promise<Result<ConsentRecord, 'client_not_found'>> {
    const client = await d.storage.repo('clients').get(input.clientId);
    if (!client) return fail('client_not_found');
    const records = await d.storage.repo('consents').list({ where: { clientId: client.id } });
    const active = activeConsent(records, client.id, input.purpose);
    if (active) return ok(active);
    const t = now();
    const row: ConsentRecord = {
      id: newId(d.clock.now()), businessId: client.businessId, createdAt: t, updatedAt: t, clientId: client.id, channel: 'whatsapp',
      purpose: input.purpose, wordingVersion: input.wordingVersion ?? CONSENT_WORDING_VERSION, grantedAt: t, revokedAt: null, source: input.source,
    };
    await d.storage.repo('consents').put(row);
    return ok(row);
  }

  /** Revokes at once ("הסר"): queued messages of that purpose become suppressed immediately. */
  async function revokeConsent(input: { clientId: Id; purpose: ConsentPurpose; source: ConsentSource }): Promise<Result<{ revoked: number }, 'client_not_found'>> {
    const client = await d.storage.repo('clients').get(input.clientId);
    if (!client) return fail('client_not_found');
    const t = now();
    const active = (await d.storage.repo('consents').list({ where: { clientId: client.id } })).filter((r) => r.purpose === input.purpose && r.revokedAt === null);
    for (const r of active) await d.storage.repo('consents').put({ ...r, revokedAt: t, revokedSource: input.source, updatedAt: t });
    const queued = await d.storage.repo('messages').list({ where: { clientId: client.id, status: 'queued' }, filter: (m) => m.purpose === input.purpose });
    for (const m of queued) await d.messaging.markSuppressed(m.id, 'no_consent');
    return ok({ revoked: active.length });
  }

  const listConsents = async (clientId: Id): Promise<ConsentRecord[]> =>
    (await d.storage.repo('consents').list({ where: { clientId } })).sort((a, b) => a.grantedAt.localeCompare(b.grantedAt));

  /** Applies the full consent state of a booking form (checkboxes are the source of truth). */
  async function syncConsents(client: Client, wanted: Record<ConsentPurpose, boolean>, source: ConsentSource): Promise<void> {
    for (const purpose of ['reminders', 'marketing'] as const) {
      const active = activeConsent(await listConsents(client.id), client.id, purpose);
      if (wanted[purpose] && !active) await recordConsent({ clientId: client.id, purpose, source });
      if (!wanted[purpose] && active) await revokeConsent({ clientId: client.id, purpose, source });
    }
  }

  // ------------------------------------------------------------ privacy

  /**
   * Erases personal data (owner on the client's behalf, or the client through eraseMyData). Future appointments are cancelled with no
   * fee and a full deposit refund (nobody is left to serve); every appointment row stays as an anonymous statistic (time, service,
   * staff, price, status, payment) without note, name link or working manage link. Messages, consents (ledger) and waitlist rows go.
   */
  async function deleteClientData(clientId: Id): Promise<Result<{ messages: number; consents: number; cancelledAppointments: number }, 'client_not_found'>> {
    const client = await d.storage.repo('clients').get(clientId);
    if (!client) return fail('client_not_found');
    let cancelled = 0;
    for (const a of await d.storage.repo('appointments').list({ where: { clientId } })) {
      if (isFutureLive(a, d.clock.now()) && await cancelWithoutFee(d, a.id, 'client_erased')) cancelled++;
    }
    const t = now();
    const messages = await d.storage.repo('messages').list({ where: { clientId } });
    const consents = await d.storage.repo('consents').list({ where: { clientId } });
    const entries = await d.storage.repo('waitlistEntries').list({ where: { clientId } });
    const appts = await d.storage.repo('appointments').list({ where: { clientId } });
    await d.storage.transact(['clients', 'messages', 'consents', 'waitlistEntries', 'waitlistOffers', 'appointments'], async (tx) => {
      await tx.put('clients', { ...client, name: '', phone: '', workNotes: '', sensitiveNotes: '', tags: [], deletedAt: t, updatedAt: t });
      for (const m of messages) await tx.delete('messages', m.id);
      for (const c of consents) await tx.delete('consents', c.id);
      for (const e of entries) await tx.delete('waitlistEntries', e.id);
      for (const o of await tx.list('waitlistOffers', { where: { businessId: client.businessId }, filter: (x) => x.clientId === clientId })) await tx.delete('waitlistOffers', o.id);
      for (const a of appts) {
        const { note: _note, ...rest } = a;
        await tx.put('appointments', { ...rest, manageToken: makeToken(), updatedAt: t });
      }
    });
    return ok({ messages: messages.length, consents: consents.length, cancelledAppointments: cancelled });
  }

  /** Export for the client (data portability). Sensitive notes only when `includeSensitive` is chosen. */
  async function exportClientData(clientId: Id, opts: { includeSensitive?: boolean } = {}): Promise<Result<ClientExport, 'client_not_found'>> {
    const client = await d.storage.repo('clients').get(clientId);
    if (!client) return fail('client_not_found');
    const { sensitiveNotes, ...rest } = client;
    return ok({
      exportedAt: now(),
      client: opts.includeSensitive ? { ...rest, sensitiveNotes } : rest,
      appointments: await getClientHistory(clientId),
      consents: await listConsents(clientId),
      messages: await d.storage.repo('messages').list({ where: { clientId } }),
      waitlistEntries: await d.storage.repo('waitlistEntries').list({ where: { clientId } }),
    });
  }

  /** Parses CSV text (header optional: name,phone,email). Dedupes by normalized phone, within the file and against existing clients. */
  async function importClientsCsv(businessId: Id, csv: string): Promise<Result<{ added: number; skipped: number; errors: { row: number; reason: string }[] }, 'business_not_found'>> {
    if (!(await d.storage.repo('businesses').get(businessId))) return fail('business_not_found');
    const rows = parseCsv(csv.replace(/^\uFEFF/, ''));
    let cols = { name: 0, phone: 1, email: 2 };
    const head = rows[0]?.map((c) => c.trim().toLowerCase());
    if (head && head.some((c) => ['name', 'phone', 'email', 'שם', 'טלפון'].includes(c))) {
      const idx = (names: string[], dflt: number): number => { const i = head.findIndex((c) => names.includes(c)); return i < 0 ? dflt : i; };
      cols = { name: idx(['name', 'שם'], 0), phone: idx(['phone', 'mobile', 'טלפון', 'נייד'], 1), email: idx(['email', 'e-mail', 'מייל', 'אימייל'], 2) };
      rows.shift();
    }
    let added = 0, skipped = 0;
    const errors: { row: number; reason: string }[] = [];
    const seen = new Set<string>();
    const offset = head && rows.length >= 0 && head.some((c) => ['name', 'phone', 'email', 'שם', 'טלפון'].includes(c)) ? 2 : 1;
    for (const [i, r] of rows.entries()) {
      if (r.every((c) => c.trim() === '')) continue;
      const rowNo = i + offset;
      const name = (r[cols.name] ?? '').trim();
      const phone = normalizeIsraeliPhone(r[cols.phone] ?? '');
      if (name.length < 2) { errors.push({ row: rowNo, reason: 'invalid_name' }); continue; }
      if (!phone.ok) { errors.push({ row: rowNo, reason: 'invalid_phone' }); continue; }
      if (seen.has(phone.e164) || (await findByPhone(businessId, phone.e164))) { skipped++; seen.add(phone.e164); continue; }
      seen.add(phone.e164);
      const email = (r[cols.email] ?? '').trim();
      await findOrCreate(businessId, name, phone.e164, email ? { workNotes: `email: ${email}` } : {});
      added++;
    }
    return ok({ added, skipped, errors });
  }

  return { importClientsCsv, upsertClient, searchClients, getClientHistory, recordConsent, revokeConsent, listConsents, deleteClientData, exportClientData, findOrCreate, syncConsents };
}

/** Minimal RFC-4180 style parser: quoted fields, doubled quotes, CRLF/LF, comma or semicolon/tab separator. */
export function parseCsv(text: string): string[][] {
  const first = text.split(/\r?\n/, 1)[0] ?? '';
  const sep = !first.includes(',') && first.includes(';') ? ';' : !first.includes(',') && first.includes('\t') ? '\t' : ',';
  const rows: string[][] = [];
  let row: string[] = [], cell = '', q = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i]!;
    if (q) { if (c === '"') { if (text[i + 1] === '"') { cell += '"'; i++; } else q = false; } else cell += c; }
    else if (c === '"') q = true;
    else if (c === sep) { row.push(cell); cell = ''; }
    else if (c === '\n' || c === '\r') { if (c === '\r' && text[i + 1] === '\n') i++; row.push(cell); rows.push(row); row = []; cell = ''; }
    else cell += c;
  }
  if (cell !== '' || row.length) { row.push(cell); rows.push(row); }
  return rows;
}
